import { NodeType } from '@amplify/amp-module';
import NodeHandlerBase from '../node-handler-base';
import { endpoints } from '../../../../constants/endpoints';
import chatbotPluginStore from './chatbot-plugin-store';
import { promiseTimeout } from '../../../../utils';

const EXECUTION_TIMEOUT = 5000;

export default class PluginHandler extends NodeHandlerBase {
  constructor(ctx) {
    super(ctx, NodeType.plugin);
    this.ctx = ctx;

    if (!chatbotPluginStore.registered) {
      this.store.registerModule('chatbotPluginStore', chatbotPluginStore);
      chatbotPluginStore.registered = true;
    }
  }

  async handleNode(node) {
    this.store.dispatch('chatbotPluginStore/setIsLoading', true);
    const { id, parentId, sortIndex, storage, pluginId } = node;
    const executeEndpoint = endpoints.plugin.execute(pluginId);
    const clientId = localStorage.getItem('clientId');
    const context = this.store.getters['chatbotStore/getStorage'];
    const { organizationId } = this.store.getters['chatbotStore/currentAmp'];

    const pluginChannel = `plugin/${pluginId}/${clientId}`;

    if (!this.store.state.chatbotStore.ablyReady) {
      await new Promise((resolve) => {
        this.unwatchAbly = this.store.watch(
          (localstate) => localstate.chatbotStore.ablyReady,
          (isReady) => {
            if (isReady) {
              resolve(isReady);
              this.unwatchAbly();
            }
          }
        );
      });
    }

    const errorNode = {
      html: '<p><em>Error: Plugin timed out</em></p>',
      type: 'message',
      id,
      parentId,
      sortIndex,
      storage,
      pluginResolved: true,
    };

    let nextNode = null;

    try {
      const nextNodePromise = new Promise(async (resolve) => {
        this.ctx.$realtime.subscribeToOrganizationChannel(
          {
            channel: pluginChannel,
            cb: (message) => {
              const {
                data: { response: messageResponse },
              } = message;
              const { contextUpdates = {}, ...response } = messageResponse || {};
              const { id, parentId, sortIndex, storage } = node;

              const newNode =
                message.name === 'error'
                  ? errorNode
                  : {
                      ...response,
                      id,
                      parentId,
                      sortIndex,
                      storage,
                      pluginResolved: true,
                    };

              Object.keys(contextUpdates).forEach((cKey) => {
                const keyId = this.store.getters['chatbotStore/getStorageId'](cKey);
                if (keyId) {
                  this.store.dispatch('chatbotStore/addToStorage', { key: keyId, data: contextUpdates[cKey] });
                } else {
                  console.warn(`[Plugin: ${pluginId}] Tried to add value to an unexisting storage key: ${cKey}`);
                }
              });

              if (newNode.type === NodeType.embedded) newNode.delay = 0;

              this.store.dispatch('chatbotStore/setDelayedState', {
                nodeId: node.id,
                delayed: true,
              });

              this.endPluginHandler(pluginChannel, organizationId);
              resolve(newNode);
            },
          },
          organizationId
        );

        await this.ctx.$amplifyApi.post(executeEndpoint, {
          clientId,
          organizationId,
          params: { settings: node.settings, context },
        });
      });

      nextNode = await promiseTimeout(nextNodePromise, EXECUTION_TIMEOUT);
    } catch (err) {
      // Plugin timed out - e.g. no response from the backend
      this.endPluginHandler(pluginChannel, organizationId);
      nextNode = errorNode;
    }

    return nextNode;
  }

  endPluginHandler(pluginChannel, organizationId) {
    this.store.dispatch('chatbotPluginStore/setIsLoading', false);

    this.ctx.$realtime.unsubscribeFromOrganizationChannel(
      {
        channel: pluginChannel,
      },
      organizationId
    );
  }
}
